<template>
  <PageLayout :title="pageTitle" :subtitle="$t('composer.subtitle')">
    <div class="row composer-row">
      <div class="col-lg-5 order-lg-2 order-1">
        <InstagramPreview
          :type="form.type"
          :username="selectedUsername"
          :avatar-url="avatarUrl"
          :media-src="previewSrc"
          :is-video="isVideo"
          :caption="form.caption"
          :hashtags="hashtagList"
        />
      </div>

      <div class="col-lg-7 order-lg-1 order-2">
        <ContentCard>
          <form @submit.prevent>
            <div class="row">
              <div class="col-md-7 mb-3">
                <label class="form-label">{{ $t('global.account') }}</label>
                <select v-model="form.social_account_id" class="form-control" required :disabled="isEdit">
                  <option value="" disabled>{{ $t('composer.select_account') }}</option>
                  <option v-for="acc in accounts" :key="acc.id" :value="acc.id">@{{ acc.username }}</option>
                </select>
              </div>
              <div class="col-md-5 mb-3">
                <label class="form-label">{{ $t('global.type') }}</label>
                <select v-model="form.type" class="form-control">
                  <option value="feed">{{ $t('composer.feed') }}</option>
                  <option value="story">{{ $t('composer.story') }}</option>
                  <option value="reel">{{ $t('composer.reel') }}</option>
                </select>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">{{ $t('composer.media') }}</label>
              <div
                class="media-dropzone"
                :class="{ 'is-dragging': dragging, 'has-media': !!previewSrc }"
                @dragover.prevent="dragging = true"
                @dragleave.prevent="dragging = false"
                @drop.prevent="onDrop"
                @click="$refs.fileInput.click()"
              >
                <input
                  ref="fileInput"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime"
                  class="d-none"
                  @change="onFileSelect"
                />
                <div v-if="uploading" class="text-muted">{{ $t('composer.uploading') }}</div>
                <div v-else-if="previewSrc" class="media-dropzone-preview">
                  <i class="fas fa-check-circle text-success fa-2x mb-2 d-block"></i>
                  <div class="small text-muted">{{ $t('composer.preview_media_ready') }}</div>
                  <div class="mt-1 small text-muted">{{ $t('composer.replace_media') }}</div>
                </div>
                <div v-else class="text-muted">
                  <i class="fas fa-cloud-upload-alt fa-2x mb-2 d-block"></i>
                  {{ $t('composer.drop_media') }}
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">{{ $t('posts.caption') }}</label>
              <textarea v-model="form.caption" class="form-control" rows="5" maxlength="2200" :placeholder="$t('composer.caption_placeholder')"></textarea>
              <small class="text-muted">{{ form.caption.length }}/2200</small>
            </div>

            <div class="mb-3">
              <label class="form-label">{{ $t('composer.hashtags') }}</label>
              <input v-model="hashtagsInput" type="text" class="form-control" :placeholder="$t('composer.hashtags_placeholder')" />
            </div>

            <div v-if="!isEdit" class="mb-3 recurring-toggle">
              <div class="custom-control custom-switch recurring-switch">
                <input
                  id="recurringSwitch"
                  v-model="isRecurring"
                  type="checkbox"
                  class="custom-control-input"
                />
                <label class="custom-control-label font-weight-bold" for="recurringSwitch">
                  {{ $t('composer.recurring_toggle') }}
                </label>
              </div>
              <small class="d-block text-muted mt-1 pl-switch-hint">{{ $t('composer.recurring_hint') }}</small>
            </div>

            <div v-if="!isEdit && isRecurring" class="recurring-panel mb-4">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">{{ $t('common.name') }}</label>
                  <input v-model="recurring.name" class="form-control" :placeholder="$t('composer.recurring_name_placeholder')" required />
                </div>
                <div class="col-md-3 mb-3">
                  <label class="form-label">{{ $t('composer.frequency') }}</label>
                  <select v-model="recurring.frequency" class="form-control">
                    <option value="daily">{{ $t('composer.daily') }}</option>
                    <option value="weekly">{{ $t('composer.weekly') }}</option>
                    <option value="monthly">{{ $t('composer.monthly') }}</option>
                  </select>
                </div>
                <div class="col-md-3 mb-3">
                  <label class="form-label">{{ $t('composer.time') }}</label>
                  <input v-model="recurring.time_of_day" type="time" class="form-control" required />
                </div>
                <div v-if="recurring.frequency === 'weekly'" class="col-md-6 mb-3">
                  <label class="form-label">{{ $t('composer.day_of_week') }}</label>
                  <select v-model.number="recurring.day_of_week" class="form-control">
                    <option v-for="d in weekDays" :key="d.value" :value="d.value">{{ d.label }}</option>
                  </select>
                </div>
                <div v-if="recurring.frequency === 'monthly'" class="col-md-6 mb-3">
                  <label class="form-label">{{ $t('composer.day_of_month') }}</label>
                  <input v-model.number="recurring.day_of_month" type="number" min="1" max="28" class="form-control" />
                </div>
              </div>
            </div>

            <div v-if="!isRecurring" class="mb-4">
              <label class="form-label">{{ $t('composer.schedule_optional') }}</label>
              <input v-model="form.scheduled_at" type="datetime-local" class="form-control" />
              <small class="text-muted">{{ $t('composer.schedule_hint') }}</small>
            </div>

            <div class="d-flex flex-wrap composer-actions">
              <template v-if="!isEdit && isRecurring">
                <button
                  type="button"
                  class="btn btn-primary"
                  :disabled="saving || !canSubmitRecurring"
                  @click="onSaveRecurring"
                >
                  <i class="fas fa-redo mr-1"></i> {{ saving ? $t('composer.saving') : $t('composer.create_recurring') }}
                </button>
              </template>
              <template v-else>
                <button
                  v-if="!isEdit"
                  type="button"
                  class="btn btn-success"
                  :disabled="saving || !canSubmit"
                  @click="onSubmit({ publishNow: true })"
                >
                  <i class="fas fa-paper-plane mr-1"></i> {{ saving ? $t('composer.publishing') : $t('composer.send_now') }}
                </button>
                <button
                  v-if="!isEdit"
                  type="button"
                  class="btn btn-primary"
                  :disabled="saving || !canSubmit || !form.scheduled_at"
                  @click="onSubmit({ publishNow: false, schedule: true })"
                >
                  <i class="fas fa-clock mr-1"></i> {{ $t('composer.schedule') }}
                </button>
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  :disabled="saving || !canSubmit"
                  @click="onSubmit({ publishNow: false, schedule: false })"
                >
                  {{ isEdit ? $t('composer.save_changes') : $t('composer.save_draft') }}
                </button>
              </template>
              <router-link :to="isEdit ? `/posts/${postId}` : '/posts'" class="btn btn-link">{{ $t('common.cancel') }}</router-link>
            </div>
          </form>
        </ContentCard>
      </div>
    </div>
  </PageLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import i18nPage from '@/mixins/i18nPage'
import InstagramPreview from '@/components/social/InstagramPreview.vue'

export default {
  components: { InstagramPreview },
  mixins: [i18nPage],
  data() {
    return {
      saving: false,
      uploading: false,
      dragging: false,
      mediaUrl: '',
      localPreview: '',
      isVideo: false,
      hashtagsInput: '',
      isRecurring: false,
      form: { social_account_id: '', type: 'feed', caption: '', scheduled_at: '' },
      recurring: {
        name: '',
        frequency: 'daily',
        time_of_day: '09:00',
        day_of_week: 1,
        day_of_month: 1,
      },
    }
  },
  computed: {
    ...mapGetters('app', ['accounts']),
    pageTitle() {
      return this.isEdit ? this.$t('composer.edit_title') : this.$t('composer.create_title')
    },
    weekDays() {
      return this.weekdayOptions()
    },
    isEdit() { return !!this.$route.params.id },
    postId() { return this.$route.params.id },
    selectedAccount() {
      return this.accounts.find((a) => String(a.id) === String(this.form.social_account_id))
    },
    selectedUsername() {
      return this.selectedAccount?.username || 'username'
    },
    avatarUrl() {
      return this.selectedAccount?.profile_picture_url
        || 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><circle cx="20" cy="20" r="20" fill="#dbdbdb"/><circle cx="20" cy="16" r="8" fill="#fff"/><ellipse cx="20" cy="36" rx="12" ry="8" fill="#fff"/></svg>')
    },
    previewSrc() {
      return this.localPreview || this.mediaUrl || ''
    },
    hashtagList() {
      return this.hashtagsInput.split(/[,\s]+/).map((t) => t.replace(/^#/, '').trim()).filter(Boolean)
    },
    canSubmit() {
      return !!this.form.social_account_id && (!!this.mediaUrl || this.isEdit)
    },
    canSubmitRecurring() {
      return this.canSubmit && !!this.recurring.name.trim() && !!this.recurring.time_of_day
    },
  },
  async created() {
    await this.fetchAccounts()
    if (this.$route.query.recurring === '1' || this.$route.query.recurring === 'true') {
      this.isRecurring = true
    }
    if (this.$route.query.schedule && !this.isEdit) {
      this.form.scheduled_at = `${this.$route.query.schedule}T09:00`
    }
    if (this.isEdit) {
      const data = await this.fetchPost(this.postId)
      const post = data.post
      this.form = {
        social_account_id: post.social_account_id,
        type: post.type,
        caption: post.caption,
        scheduled_at: post.scheduled_at ? this.toLocalInput(post.scheduled_at) : '',
      }
      this.mediaUrl = post.media_urls?.[0] || ''
      this.isVideo = /\.(mp4|mov)(\?|$)/i.test(this.mediaUrl)
      this.hashtagsInput = (post.hashtags || []).join(', ')
    } else if (this.accounts.length === 1) {
      this.form.social_account_id = this.accounts[0].id
    }
  },
  beforeDestroy() {
    this.revokeLocal()
  },
  methods: {
    ...mapActions('app', ['fetchAccounts']),
    ...mapActions('posts', ['createPost', 'updatePost', 'fetchPost', 'uploadMedia']),
    ...mapActions('recurring', ['createItem']),
    toLocalInput(iso) {
      const d = new Date(iso)
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    revokeLocal() {
      if (this.localPreview && this.localPreview.startsWith('blob:')) {
        URL.revokeObjectURL(this.localPreview)
      }
    },
    onDrop(e) {
      this.dragging = false
      const file = e.dataTransfer?.files?.[0]
      if (file) this.uploadFile(file)
    },
    onFileSelect(e) {
      const file = e.target.files?.[0]
      if (file) this.uploadFile(file)
      e.target.value = ''
    },
    async uploadFile(file) {
      this.uploading = true
      this.revokeLocal()
      this.localPreview = URL.createObjectURL(file)
      this.isVideo = file.type.startsWith('video/')
      try {
        const data = await this.uploadMedia(file)
        this.mediaUrl = data.url
      } catch (err) {
        this.revokeLocal()
        this.localPreview = ''
        await this.$appAlert(err.response?.data?.message || this.$t('composer.upload_failed'))
      } finally {
        this.uploading = false
      }
    },
    buildPayload({ publishNow, schedule }) {
      const payload = {
        caption: this.form.caption,
        type: this.form.type,
        media_urls: this.mediaUrl ? [this.mediaUrl] : [],
        hashtags: this.hashtagList,
        scheduled_at: null,
        publish_now: false,
      }
      if (publishNow) {
        payload.publish_now = true
      } else if (schedule && this.form.scheduled_at) {
        payload.scheduled_at = new Date(this.form.scheduled_at).toISOString()
      } else if (this.form.scheduled_at && this.isEdit) {
        payload.scheduled_at = new Date(this.form.scheduled_at).toISOString()
      }
      return payload
    },
    async onSaveRecurring() {
      if (!this.canSubmitRecurring) return
      if (!this.mediaUrl) {
        await this.$appAlert(this.$t('composer.media_required'))
        return
      }
      this.saving = true
      try {
        const time = this.recurring.time_of_day.length >= 5
          ? this.recurring.time_of_day.slice(0, 5)
          : this.recurring.time_of_day
        await this.createItem({
          name: this.recurring.name.trim(),
          social_account_id: Number(this.form.social_account_id),
          type: this.form.type,
          caption: this.form.caption,
          media_urls: [this.mediaUrl],
          hashtags: this.hashtagList,
          frequency: this.recurring.frequency,
          time_of_day: time,
          day_of_week: this.recurring.day_of_week,
          day_of_month: this.recurring.day_of_month,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          requires_approval: false,
        })
        this.$router.push('/recurring')
      } catch (e) {
        await this.$appAlert(e.response?.data?.message || this.$t('composer.create_recurring_failed'))
      } finally {
        this.saving = false
      }
    },
    async onSubmit({ publishNow = false, schedule = false } = {}) {
      if (!this.canSubmit) return
      if (schedule && !this.form.scheduled_at) {
        await this.$appAlert(this.$t('composer.pick_schedule'))
        return
      }
      if (!this.isEdit && !this.mediaUrl) {
        await this.$appAlert(this.$t('composer.media_required'))
        return
      }
      this.saving = true
      try {
        const payload = this.buildPayload({ publishNow, schedule })
        if (this.isEdit) {
          await this.updatePost({ id: this.postId, payload })
          this.$router.push(`/posts/${this.postId}`)
        } else {
          const post = await this.createPost({
            social_account_id: Number(this.form.social_account_id),
            ...payload,
          })
          if (publishNow && post.status === 'publishing') {
            this.$router.push(`/posts/${post.id}`)
          } else {
            this.$router.push('/posts')
          }
        }
      } catch (e) {
        await this.$appAlert(e.response?.data?.message || this.$t('composer.save_failed'))
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.composer-row {
  align-items: flex-start;
}

.composer-actions { gap: 0.5rem; }
.composer-actions .btn { margin-right: 0; }

.recurring-toggle {
  padding: 0.85rem 1rem;
  border: 1px solid #e8ecef;
  border-radius: 10px;
  background: #fafbfc;
}
.recurring-switch {
  padding-left: 3rem;
  min-height: 1.5rem;
  display: flex;
  align-items: center;
}
.recurring-switch .custom-control-label {
  position: relative;
  margin-bottom: 0;
  line-height: 1.4;
  cursor: pointer;
}
.recurring-switch .custom-control-label::before {
  left: -3rem;
  top: 0.1rem;
  width: 2.5rem;
  height: 1.35rem;
  border-radius: 999px;
  border: 1px solid #ced4da;
  background-color: #e9ecef;
  pointer-events: all;
}
.recurring-switch .custom-control-label::after {
  top: calc(0.1rem + 3px);
  left: calc(-3rem + 3px);
  width: calc(1.35rem - 6px);
  height: calc(1.35rem - 6px);
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.15s ease-in-out, background-color 0.15s ease-in-out;
}
.recurring-switch .custom-control-input:checked ~ .custom-control-label::before {
  background-color: var(--wc-primary);
  border-color: var(--wc-primary);
}
.recurring-switch .custom-control-input:checked ~ .custom-control-label::after {
  background-color: #fff;
  transform: translateX(1.15rem);
}
.recurring-switch .custom-control-input:focus ~ .custom-control-label::before {
  box-shadow: 0 0 0 0.15rem rgba(var(--wc-primary-rgb), 0.25);
}
.pl-switch-hint {
  padding-left: 3rem;
}
.recurring-panel {
  padding: 1rem;
  border: 1px solid #d8d8d8;
  border-radius: 10px;
  background: #f5f5f5;
}

.media-dropzone {
  border: 2px dashed #cfcfcf;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  background: #fafafa;
  transition: border-color 0.15s, background 0.15s;
}
.media-dropzone.is-dragging,
.media-dropzone:hover { border-color: #1a1a1a; background: #f0f0f0; }
.media-dropzone.has-media { padding: 1rem; }
</style>
